import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  FaCheck,
  FaCheckCircle,
  FaClock,
  FaEnvelope,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaInfoCircle,
  FaLink,
  FaPaperPlane,
  FaQrcode,
  FaShieldAlt,
  FaSignOutAlt,
  FaSpinner,
  FaTimes,
  FaTimesCircle,
  FaUser,
  FaUserCheck,
  FaUsers,
} from "react-icons/fa";
import { QrReader } from "react-qr-reader";
import { useTranslation } from "react-i18next";

import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import {
  deleteAdherent,
  extractGroupIdFromInviteLink,
  getUserRequests,
  joinGroupByLink,
} from "../../../services/groupServices";
import { getAdherentsByUser } from "../../../services/adherentServices";
import { sendNotificationToAdmin } from "../../../services/notificationServices";
import { useNotification } from "../../../components/Notification/NotificationManager";
import useScrollToTop from "../../../hooks/useScollToTop";

const DEFAULT_AVATAR = "/img/user_avatar.jpg";

function buildInviteLinkFromGroupId(groupId) {
  if (!groupId) return "";

  return `${window.location.origin}/join-group/${groupId}`;
}

function getErrorMessage(error, fallback = "Une erreur est survenue.") {
  return error?.error || error?.message || error?.details || fallback;
}

function normalizeAdherentsResponse(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.adherents)) return response.adherents;
  if (Array.isArray(response?.Adherents)) return response.Adherents;
  if (Array.isArray(response?.data)) return response.data;

  return [];
}

function getFullName(judoka) {
  return (
    [judoka?.prenom, judoka?.nom].filter(Boolean).join(" ") ||
    "Profil judoka"
  );
}

function getPhotoUrl(judoka) {
  return judoka?.photo_url || judoka?.photoURL || DEFAULT_AVATAR;
}

function getRequestGroup(request) {
  return request?.group || request?.Group || request?.Groupe || null;
}

function getRequestAdherentId(request) {
  return (
    request?.adherent_id ||
    request?.adherentId ||
    request?.Adherent?.id ||
    request?.adherent?.id ||
    request?.Judoka?.id ||
    null
  );
}

function getRequestStatusLabel(status) {
  if (status === "approved") return "Validé";
  if (status === "pending") return "En attente";
  if (status === "rejected") return "Refusé";

  return "Inconnu";
}

function getRequestStatusStyle(status) {
  if (status === "approved") {
    return "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200";
  }

  if (status === "pending") {
    return "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200";
  }

  if (status === "rejected") {
    return "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-200";
  }

  return "bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white/70";
}

function getStatusConfig(status, hasInviteTarget) {
  if (status === "approved") {
    return {
      icon: FaCheckCircle,
      eyebrow: "Groupe déjà validé",
      title: hasInviteTarget
        ? "Ce judoka est déjà dans ce groupe"
        : "Judoka déjà rattaché à un groupe",
      description: hasInviteTarget
        ? "Le coach a déjà validé ce judoka pour le groupe ciblé par ce lien. Il n’est pas nécessaire de refaire une demande."
        : "Ce judoka possède déjà au moins un groupe validé par le coach.",
      wrapperClassName:
        "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-100",
      iconClassName:
        "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200",
    };
  }

  if (status === "pending") {
    return {
      icon: FaClock,
      eyebrow: "Demande déjà envoyée",
      title: hasInviteTarget
        ? "Demande déjà en attente pour ce groupe"
        : "Demande en attente",
      description: hasInviteTarget
        ? "Une demande existe déjà pour ce judoka et ce groupe. Le coach doit encore la valider."
        : "Une demande existe déjà pour ce judoka. Le coach doit encore la valider.",
      wrapperClassName:
        "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-100",
      iconClassName:
        "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200",
    };
  }

  if (status === "rejected") {
    return {
      icon: FaTimesCircle,
      eyebrow: "Demande refusée",
      title: "Demande refusée",
      description:
        "La précédente demande a été refusée. Vous pouvez renvoyer une demande si le coach vous le demande.",
      wrapperClassName:
        "border-red-200 bg-red-50 text-red-900 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-100",
      iconClassName:
        "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-200",
    };
  }

  return {
    icon: FaShieldAlt,
    eyebrow: "Aucune demande pour ce groupe",
    title: "Choisissez un judoka puis rejoignez un groupe",
    description:
      "Sélectionnez le judoka concerné, puis scannez le QR code ou collez le lien d’invitation transmis par le coach.",
    wrapperClassName:
      "border-slate-200 bg-white text-slate-800 dark:border-white/10 dark:bg-white/[0.05] dark:text-white",
    iconClassName:
      "bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200",
  };
}

function JudokaSelector({ adherents, selectedAdherentId, onSelect, loading }) {
  if (loading) {
    return (
      <section className="rounded-[2rem] border border-slate-200 bg-white p-6 text-center shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
        <FaSpinner className="mx-auto animate-spin text-2xl text-sky-600 dark:text-sky-300" />

        <p className="mt-3 text-sm font-bold text-slate-500 dark:text-white/60">
          Chargement de vos profils judokas...
        </p>
      </section>
    );
  }

  if (!adherents.length) {
    return (
      <section className="rounded-[2rem] border border-amber-200 bg-amber-50 p-5 shadow-xl shadow-amber-500/10 dark:border-amber-500/20 dark:bg-amber-500/10">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-amber-700 dark:bg-amber-500/10 dark:text-amber-200">
            <FaExclamationTriangle />
          </div>

          <div>
            <h2 className="font-titre text-xl font-bold text-amber-950 dark:text-amber-100">
              Aucun profil judoka trouvé
            </h2>

            <p className="mt-1 text-sm font-medium leading-6 text-amber-800 dark:text-amber-100/75">
              Vous devez d’abord créer un profil judoka lié à votre compte avant
              de pouvoir rejoindre un groupe.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
      <div className="flex items-start gap-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-100 text-orange-700 dark:bg-orange-500/10 dark:text-orange-200">
          <FaUsers />
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
            Judoka concerné
          </p>

          <h2 className="mt-1 font-titre text-2xl font-bold text-slate-950 dark:text-white">
            Sélectionnez le profil à rattacher
          </h2>

          <p className="mt-2 text-sm font-medium leading-7 text-slate-500 dark:text-white/50">
            Si votre compte gère plusieurs judokas, choisissez celui qui doit
            rejoindre le groupe.
          </p>
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {adherents.map((judoka) => {
          const isSelected = String(selectedAdherentId) === String(judoka.id);

          return (
            <button
              key={judoka.id}
              type="button"
              onClick={() => onSelect(judoka.id)}
              className={`flex items-center gap-3 rounded-[1.4rem] border p-3 text-left transition ${
                isSelected
                  ? "border-orange-300 bg-orange-50 shadow-lg shadow-orange-500/10 dark:border-orange-400/30 dark:bg-orange-500/10"
                  : "border-slate-200 bg-slate-50 hover:bg-white hover:shadow-md dark:border-white/10 dark:bg-white/[0.04] dark:hover:bg-white/[0.08]"
              }`}
            >
              <img
                src={getPhotoUrl(judoka)}
                alt={getFullName(judoka)}
                className="h-12 w-12 rounded-2xl object-cover"
                onError={(event) => {
                  event.currentTarget.src = DEFAULT_AVATAR;
                }}
              />

              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-slate-950 dark:text-white">
                  {getFullName(judoka)}
                </p>

                <p className="truncate text-xs font-semibold text-slate-500 dark:text-white/50">
                  {judoka.club || judoka.genre || "Profil judoka"}
                </p>
              </div>

              {isSelected && <FaCheck className="shrink-0 text-orange-500" />}
            </button>
          );
        })}
      </div>
    </section>
  );
}

function JudokaGroupsSummary({
  selectedJudoka,
  requests,
  loading,
  onLeaveGroup,
  leavingGroupId,
}) {
  if (!selectedJudoka || loading) return null;

  if (!requests.length) {
    return (
      <section className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-white/70">
            <FaInfoCircle />
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
              Groupes du judoka
            </p>

            <h2 className="mt-1 font-titre text-xl font-bold text-slate-950 dark:text-white">
              Aucun groupe rattaché
            </h2>

            <p className="mt-2 text-sm font-medium leading-6 text-slate-500 dark:text-white/50">
              {getFullName(selectedJudoka)} n’a pas encore de demande ou de
              groupe validé.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
      <div className="flex items-start gap-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-200">
          <FaUserCheck />
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
            Groupes du judoka
          </p>

          <h2 className="mt-1 font-titre text-xl font-bold text-slate-950 dark:text-white">
            Situation actuelle de {getFullName(selectedJudoka)}
          </h2>

          <p className="mt-2 text-sm font-medium leading-6 text-slate-500 dark:text-white/50">
            Voici les demandes et rattachements déjà connus pour ce judoka.
          </p>
        </div>
      </div>

      <div className="mt-5 grid gap-3">
        {requests.map((request) => {
          const group = getRequestGroup(request);
          const groupId = group?.id || request.group_id;
          const groupName = group?.nom || `Groupe #${groupId}`;
          const isApproved = request.status === "approved";
          const isLeaving = String(leavingGroupId) === String(groupId);

          return (
            <div
              key={`${request.adherent_id}-${groupId}-${request.status}`}
              className="flex flex-col gap-3 rounded-[1.4rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-950 sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <p className="font-bold text-slate-950 dark:text-white">
                  {groupName}
                </p>

                <p className="mt-1 text-xs font-semibold text-slate-500 dark:text-white/50">
                  Groupe #{groupId}
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <span
                  className={`inline-flex min-h-9 items-center justify-center rounded-2xl px-3 text-xs font-bold ${getRequestStatusStyle(
                    request.status,
                  )}`}
                >
                  {getRequestStatusLabel(request.status)}
                </span>

                {isApproved && (
                  <button
                    type="button"
                    onClick={() => onLeaveGroup(groupId)}
                    disabled={isLeaving}
                    className="inline-flex min-h-9 items-center justify-center gap-2 rounded-2xl bg-red-600 px-3 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5 hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500"
                  >
                    {isLeaving ? (
                      <FaSpinner className="animate-spin" />
                    ) : (
                      <FaSignOutAlt />
                    )}
                    {isLeaving ? "Sortie..." : "Quitter"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function RequestStatusCard({
  request,
  loading,
  selectedJudoka,
  hasInviteTarget,
}) {
  if (loading) {
    return (
      <section className="rounded-[2rem] border border-slate-200 bg-white p-6 text-center shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
        <FaSpinner className="mx-auto animate-spin text-2xl text-sky-600 dark:text-sky-300" />

        <p className="mt-3 text-sm font-bold text-slate-500 dark:text-white/60">
          Vérification des demandes...
        </p>
      </section>
    );
  }

  const status = request?.status || null;
  const group = getRequestGroup(request);
  const groupName = group?.nom || (request?.group_id ? `Groupe #${request.group_id}` : "");
  const config = getStatusConfig(status, hasInviteTarget);
  const Icon = config.icon;

  return (
    <section
      className={`rounded-[2rem] border p-5 shadow-xl shadow-slate-200/50 dark:shadow-black/20 ${config.wrapperClassName}`}
    >
      <div className="flex items-start gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${config.iconClassName}`}
        >
          <Icon />
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] opacity-60">
            {config.eyebrow}
          </p>

          <h2 className="mt-2 font-titre text-2xl font-bold">
            {config.title}
          </h2>

          <p className="mt-2 text-sm font-medium leading-7 opacity-75">
            {config.description}
          </p>

          {selectedJudoka && (
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="inline-flex min-h-10 items-center gap-2 rounded-2xl bg-white/70 px-4 text-xs font-bold text-slate-700 shadow-sm dark:bg-white/10 dark:text-white/80">
                <FaUser />
                Judoka : {getFullName(selectedJudoka)}
              </span>

              {request && groupName && (
                <span className="inline-flex min-h-10 items-center gap-2 rounded-2xl bg-white/70 px-4 text-xs font-bold text-slate-700 shadow-sm dark:bg-white/10 dark:text-white/80">
                  <FaUserCheck />
                  Groupe : {groupName}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function JoinGroup() {
  const { t } = useTranslation();
  useScrollToTop();

  const { groupId: routeGroupId } = useParams();
  const { user } = useUser();
  const { addNotification } = useNotification();

  const scanHandledRef = useRef(false);

  const [inviteLink, setInviteLink] = useState("");
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [cameraMessage, setCameraMessage] = useState("");

  const [adherents, setAdherents] = useState([]);
  const [selectedAdherentId, setSelectedAdherentId] = useState(null);
  const [loadingAdherents, setLoadingAdherents] = useState(true);

  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [leavingGroupId, setLeavingGroupId] = useState(null);

  const userRole = user?.role_id
    ? getRoleFromRoleId(user.role_id)
    : "Utilisateur";

  const hasToken = Boolean(localStorage.getItem("token"));

  const extractedGroupId = useMemo(() => {
    return extractGroupIdFromInviteLink(inviteLink);
  }, [inviteLink]);

  const isLinkValid = Boolean(extractedGroupId);

  const selectedJudoka = useMemo(() => {
    if (!adherents.length) return null;

    return (
      adherents.find(
        (judoka) => String(judoka.id) === String(selectedAdherentId),
      ) || null
    );
  }, [adherents, selectedAdherentId]);

  const selectedJudokaRequests = useMemo(() => {
    if (!selectedAdherentId) return [];

    return requests.filter((request) => {
      const requestAdherentId = getRequestAdherentId(request);

      return String(requestAdherentId) === String(selectedAdherentId);
    });
  }, [requests, selectedAdherentId]);

  const currentInviteRequest = useMemo(() => {
    if (!selectedAdherentId || !extractedGroupId) return null;

    return (
      selectedJudokaRequests.find((request) => {
        const group = getRequestGroup(request);
        const requestGroupId = group?.id || request.group_id;

        return String(requestGroupId) === String(extractedGroupId);
      }) || null
    );
  }, [selectedAdherentId, extractedGroupId, selectedJudokaRequests]);

  const selectedRequestForStatus = useMemo(() => {
    if (currentInviteRequest) return currentInviteRequest;

    return (
      selectedJudokaRequests.find((request) => request.status === "approved") ||
      selectedJudokaRequests.find((request) => request.status === "pending") ||
      selectedJudokaRequests[0] ||
      null
    );
  }, [currentInviteRequest, selectedJudokaRequests]);

  const currentInviteStatus = currentInviteRequest?.status || null;
  const isAlreadyApprovedForInvite = currentInviteStatus === "approved";
  const isAlreadyPendingForInvite = currentInviteStatus === "pending";
  const shouldBlockSubmit =
    isAlreadyApprovedForInvite || isAlreadyPendingForInvite;

  const fetchAdherents = useCallback(async () => {
    if (!localStorage.getItem("token")) {
      setAdherents([]);
      setSelectedAdherentId(null);
      setLoadingAdherents(false);
      return;
    }

    setLoadingAdherents(true);

    try {
      const response = await getAdherentsByUser();
      const loadedAdherents = normalizeAdherentsResponse(response);

      setAdherents(loadedAdherents);

      if (loadedAdherents.length > 0) {
        setSelectedAdherentId((prevSelectedId) => {
          const keepSelected = loadedAdherents.some(
            (judoka) => String(judoka.id) === String(prevSelectedId),
          );

          return keepSelected ? prevSelectedId : loadedAdherents[0].id;
        });
      } else {
        setSelectedAdherentId(null);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des judokas :", error);

      addNotification(
        "Erreur lors de la récupération de vos profils judokas.",
        "error",
      );
    } finally {
      setLoadingAdherents(false);
    }
  }, [addNotification]);

  const fetchUserRequests = useCallback(async () => {
    if (!localStorage.getItem("token")) {
      setRequests([]);
      setLoadingRequests(false);
      return;
    }

    setLoadingRequests(true);

    try {
      const response = await getUserRequests();
      const loadedRequests = Array.isArray(response) ? response : [];

      setRequests(loadedRequests);
    } catch (error) {
      console.error("Erreur lors de la récupération des demandes :", error);

      addNotification(
        getErrorMessage(
          error,
          "Erreur lors de la récupération de vos demandes.",
        ),
        "error",
      );
    } finally {
      setLoadingRequests(false);
    }
  }, [addNotification]);

  useEffect(() => {
    fetchAdherents();
    fetchUserRequests();
  }, [fetchAdherents, fetchUserRequests]);

  useEffect(() => {
    if (routeGroupId) {
      setInviteLink(buildInviteLinkFromGroupId(routeGroupId));
    }
  }, [routeGroupId]);

  const handleLinkChange = (event) => {
    setInviteLink(event.target.value);
  };

  const handleQrScan = (result, error) => {
    if (result?.text && !scanHandledRef.current) {
      scanHandledRef.current = true;

      const scannedLink = result.text;
      const scannedGroupId = extractGroupIdFromInviteLink(scannedLink);

      if (!scannedGroupId) {
        addNotification("Le QR code scanné ne contient pas un lien valide.", "error");

        setTimeout(() => {
          scanHandledRef.current = false;
        }, 1200);

        return;
      }

      setInviteLink(scannedLink);
      setShowQrScanner(false);
      setCameraMessage("");
      addNotification("QR code scanné avec succès.", "success");

      setTimeout(() => {
        scanHandledRef.current = false;
      }, 1200);

      return;
    }

    if (error?.name === "NotAllowedError") {
      setCameraMessage(
        "Accès caméra refusé. Autorisez la caméra ou collez directement le lien d’invitation.",
      );
    }
  };

  const handleSubmitLink = async () => {
    if (!localStorage.getItem("token")) {
      addNotification("Vous devez être connecté pour rejoindre un groupe.", "error");
      return;
    }

    if (!selectedAdherentId) {
      addNotification("Veuillez sélectionner le judoka concerné.", "error");
      return;
    }

    if (!isLinkValid) {
      addNotification("Veuillez entrer un lien d’invitation valide.", "error");
      return;
    }

    if (isAlreadyApprovedForInvite) {
      addNotification(
        "Ce judoka est déjà validé dans ce groupe. Aucune nouvelle demande n’est nécessaire.",
        "info",
      );
      return;
    }

    if (isAlreadyPendingForInvite) {
      addNotification(
        "Une demande est déjà en attente pour ce judoka et ce groupe.",
        "info",
      );
      return;
    }

    setSubmitting(true);

    try {
      const response = await joinGroupByLink(inviteLink, selectedAdherentId);

      addNotification(response?.message || "Demande envoyée au coach.", "success");

      try {
        const token = localStorage.getItem("token");
        const identity = [user?.prenom, user?.nom].filter(Boolean).join(" ");
        const judokaName = selectedJudoka
          ? getFullName(selectedJudoka)
          : "Judoka";

        const message = `Un utilisateur a demandé à rejoindre un groupe via un lien d'invitation : ${
          identity || "Utilisateur"
        }${
          user?.email ? ` (${user.email})` : ""
        }. Judoka : ${judokaName}. Groupe ID : ${extractedGroupId}`;

        await sendNotificationToAdmin(message, token);
      } catch (notificationError) {
        console.warn(
          "Demande envoyée, mais notification admin non transmise :",
          notificationError,
        );
      }

      await fetchUserRequests();
    } catch (error) {
      console.error("Erreur lors de la demande de rejoindre un groupe :", error);

      addNotification(
        getErrorMessage(
          error,
          "Une erreur est survenue. Veuillez réessayer.",
        ),
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleLeaveGroup = async (groupId) => {
    if (!selectedAdherentId) {
      addNotification("Judoka introuvable.", "error");
      return;
    }

    if (!groupId) {
      addNotification("Groupe introuvable.", "error");
      return;
    }

    setLeavingGroupId(groupId);

    try {
      const response = await deleteAdherent(selectedAdherentId, groupId);

      addNotification(
        response?.message || "Le judoka a quitté le groupe avec succès.",
        "success",
      );

      await fetchUserRequests();
    } catch (error) {
      console.error("Erreur lors de la sortie du groupe :", error);

      addNotification(
        getErrorMessage(
          error,
          "Une erreur est survenue lors de la suppression.",
        ),
        "error",
      );
    } finally {
      setLeavingGroupId(null);
    }
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-50 px-4 pb-16 pt-28 font-satoshi dark:bg-slate-950 sm:px-6 lg:px-8">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(249,115,22,0.14),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(37,99,235,0.16),transparent_32%)]" />
      <div className="absolute inset-x-0 top-0 h-72 bg-slate-950" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-400/80 to-transparent" />

      <section className="relative mx-auto max-w-6xl">
        <div className="mb-7 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] text-orange-200 shadow-sm backdrop-blur-xl">
              <FaQrcode />
              Invitation groupe
            </div>

            <h1 className="mt-4 font-titre text-4xl font-bold leading-tight text-white sm:text-5xl">
              {t("joinGroup.title", {
                defaultValue: "Rejoindre un groupe",
              })}
            </h1>

            <p className="mt-3 max-w-2xl text-base font-medium leading-8 text-white/60 sm:text-lg">
              {t("joinGroup.description", {
                defaultValue:
                  "Sélectionnez le judoka concerné, puis scannez le QR code ou collez le lien d’invitation transmis par votre coach.",
              })}
            </p>
          </div>

          {user && (
            <div className="flex items-center gap-4 rounded-[1.5rem] border border-white/10 bg-white/10 p-4 text-white shadow-xl backdrop-blur-xl">
              <img
                src={user.photoURL || user.photo_url || DEFAULT_AVATAR}
                alt={`${user.prenom || ""} ${user.nom || ""}`}
                className="h-14 w-14 rounded-2xl border border-white/20 object-cover"
                onError={(event) => {
                  event.currentTarget.src = DEFAULT_AVATAR;
                }}
              />

              <div>
                <p className="font-bold text-white">
                  {[user.prenom, user.nom].filter(Boolean).join(" ") ||
                    "Utilisateur"}
                </p>

                <p className="mt-1 text-xs font-semibold text-white/60">
                  Formule actuelle : {userRole}
                </p>
              </div>
            </div>
          )}
        </div>

        {!hasToken && (
          <section className="mb-6 rounded-[2rem] border border-amber-200 bg-amber-50 p-5 shadow-xl shadow-amber-500/10 dark:border-amber-500/20 dark:bg-amber-500/10">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-amber-700 dark:bg-amber-500/10 dark:text-amber-200">
                <FaExclamationTriangle />
              </div>

              <div>
                <h2 className="font-titre text-xl font-bold text-amber-950 dark:text-amber-100">
                  Connexion nécessaire
                </h2>

                <p className="mt-1 text-sm font-medium leading-6 text-amber-800 dark:text-amber-100/75">
                  Vous devez être connecté avec votre compte avant de rejoindre
                  un groupe.
                </p>
              </div>
            </div>
          </section>
        )}

        <div className="space-y-6">
          <JudokaSelector
            adherents={adherents}
            selectedAdherentId={selectedAdherentId}
            onSelect={setSelectedAdherentId}
            loading={loadingAdherents}
          />

          <JudokaGroupsSummary
            selectedJudoka={selectedJudoka}
            requests={selectedJudokaRequests}
            loading={loadingRequests}
            onLeaveGroup={handleLeaveGroup}
            leavingGroupId={leavingGroupId}
          />

          <RequestStatusCard
            request={selectedRequestForStatus}
            loading={loadingRequests}
            selectedJudoka={selectedJudoka}
            hasInviteTarget={Boolean(extractedGroupId)}
          />

          {adherents.length > 0 && (
            <section className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
              <article className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
                <div className="flex items-start gap-3">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200">
                    <FaQrcode />
                  </div>

                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                      Scan QR code
                    </p>

                    <h2 className="mt-1 font-titre text-2xl font-bold text-slate-950 dark:text-white">
                      Scanner l’invitation
                    </h2>

                    <p className="mt-2 text-sm font-medium leading-7 text-slate-500 dark:text-white/50">
                      Utilisez la caméra de votre téléphone pour scanner le QR
                      code affiché par votre coach.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    scanHandledRef.current = false;
                    setCameraMessage("");
                    setShowQrScanner((current) => !current);
                  }}
                  className="mt-5 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 to-orange-500 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5 active:translate-y-0"
                >
                  {showQrScanner ? <FaTimes /> : <FaQrcode />}
                  {showQrScanner ? "Fermer le scanner" : "Scanner un QR code"}
                </button>

                {cameraMessage && (
                  <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-bold leading-6 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-100">
                    {cameraMessage}
                  </div>
                )}

                {showQrScanner && (
                  <div className="mt-5 overflow-hidden rounded-[1.5rem] border border-slate-200 bg-slate-50 p-2 dark:border-white/10 dark:bg-slate-950">
                    <QrReader
                      constraints={{
                        facingMode: "environment",
                      }}
                      onResult={handleQrScan}
                      containerStyle={{
                        width: "100%",
                      }}
                      videoStyle={{
                        width: "100%",
                      }}
                    />
                  </div>
                )}

                {!showQrScanner && (
                  <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-950">
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-sky-700 dark:bg-white/10 dark:text-sky-200">
                        <FaInfoCircle />
                      </div>

                      <p className="text-sm font-medium leading-6 text-slate-500 dark:text-white/50">
                        Si la caméra ne fonctionne pas, vous pouvez coller le
                        lien d’invitation directement dans le champ à droite.
                      </p>
                    </div>
                  </div>
                )}
              </article>

              <article className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
                <div className="flex items-start gap-3">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200">
                    <FaLink />
                  </div>

                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                      Lien d’invitation
                    </p>

                    <h2 className="mt-1 font-titre text-2xl font-bold text-slate-950 dark:text-white">
                      Coller le lien du coach
                    </h2>

                    <p className="mt-2 text-sm font-medium leading-7 text-slate-500 dark:text-white/50">
                      Le lien doit ressembler à{" "}
                      <span className="font-bold text-slate-700 dark:text-white/70">
                        /join-group/62
                      </span>
                      . Vous pouvez aussi saisir directement l’ID du groupe.
                    </p>
                  </div>
                </div>

                <div className="mt-5">
                  <label
                    htmlFor="inviteLink"
                    className="mb-2 block text-xs font-bold uppercase tracking-[0.16em] text-slate-400"
                  >
                    Lien ou ID du groupe
                  </label>

                  <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
                    <div className="relative">
                      <FaExternalLinkAlt className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

                      <input
                        type="text"
                        id="inviteLink"
                        value={inviteLink}
                        onChange={handleLinkChange}
                        placeholder="https://www.judo-coach-pro.com/join-group/62"
                        className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm font-bold text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-sky-300 focus:bg-white focus:ring-4 focus:ring-sky-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-sky-300/50 dark:focus:ring-sky-500/10"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={handleSubmitLink}
                      disabled={
                        !selectedAdherentId ||
                        !isLinkValid ||
                        submitting ||
                        !hasToken ||
                        shouldBlockSubmit
                      }
                      className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-emerald-500/20 transition hover:-translate-y-0.5 hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500"
                    >
                      {submitting ? (
                        <FaSpinner className="animate-spin" />
                      ) : shouldBlockSubmit ? (
                        <FaCheckCircle />
                      ) : (
                        <FaPaperPlane />
                      )}

                      {submitting
                        ? "Envoi..."
                        : shouldBlockSubmit
                          ? "Déjà demandé"
                          : "Envoyer"}
                    </button>
                  </div>

                  <div
                    className={`mt-4 rounded-2xl border p-4 text-sm font-bold leading-6 ${
                      inviteLink
                        ? isLinkValid
                          ? shouldBlockSubmit
                            ? "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-100"
                            : "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200"
                          : "border-red-200 bg-red-50 text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-200"
                        : "border-slate-200 bg-slate-50 text-slate-500 dark:border-white/10 dark:bg-slate-950 dark:text-white/50"
                    }`}
                  >
                    {inviteLink
                      ? isLinkValid
                        ? isAlreadyApprovedForInvite
                          ? `Ce judoka est déjà validé dans le groupe #${extractedGroupId}.`
                          : isAlreadyPendingForInvite
                            ? `Une demande est déjà en attente pour le groupe #${extractedGroupId}.`
                            : `Lien valide · groupe #${extractedGroupId}`
                        : "Lien invalide. Format attendu : /join-group/ID"
                      : "En attente d’un lien d’invitation."}
                  </div>
                </div>

                <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-950">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-orange-600 dark:bg-white/10 dark:text-orange-200">
                      <FaUser />
                    </div>

                    <div>
                      <p className="text-sm font-bold text-slate-800 dark:text-white">
                        Profil sélectionné
                      </p>

                      <p className="mt-1 text-xs font-medium leading-5 text-slate-500 dark:text-white/50">
                        La demande sera envoyée pour{" "}
                        <span className="font-bold text-slate-700 dark:text-white/70">
                          {selectedJudoka
                            ? getFullName(selectedJudoka)
                            : "le judoka sélectionné"}
                        </span>
                        .
                      </p>
                    </div>
                  </div>
                </div>
              </article>
            </section>
          )}

          <section className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-200">
                <FaEnvelope />
              </div>

              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                  Fonctionnement
                </p>

                <h2 className="mt-1 font-titre text-2xl font-bold text-slate-950 dark:text-white">
                  Validation par le coach
                </h2>

                <p className="mt-2 text-sm font-medium leading-7 text-slate-500 dark:text-white/50">
                  Après l’envoi, la demande passe en attente. Le coach ou
                  l’administrateur devra l’accepter pour que le judoka soit
                  visible dans le groupe. Si le judoka est déjà validé dans le
                  groupe, la page le signale automatiquement.
                </p>
              </div>
            </div>
          </section>
        </div>
      </section>
    </main>
  );
}

export default JoinGroup;